/**
 * Making the LMS installable, and making an installed copy feel like an app.
 *
 * Three jobs:
 *   1. Point the page at the RIGHT manifest. A learner at Sthavya Learning must install
 *      "Sthavya Learning", not "MyLMS", so the <link rel="manifest"> is written at runtime
 *      once we know which tenant the page belongs to.
 *   2. Register the service worker, and surface an update instead of swapping assets
 *      under someone mid-lesson.
 *   3. Hold on to the browser's install prompt, which fires once and is lost if ignored.
 */
import { API_URL } from '../config/api';

const MANIFEST_ID = 'app-manifest';
const THEME_ID = 'app-theme-colour';

/** Standalone, iOS's older flag, or launched from a home-screen shortcut. */
export function isInstalled() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)')?.matches
    || window.matchMedia?.('(display-mode: minimal-ui)')?.matches
    || window.navigator.standalone === true
    || document.referrer.startsWith('android-app://');
}

export function isIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua)
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/**
 * Swap in the manifest for this tenant.
 *
 * The API may live on another origin (api.mylms.in), and a manifest fetched cross-origin
 * is not sent with credentials — which is fine here, because the manifest and the icons
 * are public on purpose.
 */
export function applyManifest({ subdomain, customDomain = false, themeColor } = {}) {
  if (typeof document === 'undefined') return;

  const params = new URLSearchParams();
  if (subdomain) params.set('tenant', subdomain);
  if (customDomain) params.set('custom_domain', 'true');
  // The API usually sits on another host, and a browser resolves start_url against the
  // MANIFEST's origin — so tell the server where this page actually lives, or the install
  // is rejected for pointing off-origin.
  if (window.location?.origin) params.set('origin', window.location.origin);
  const href = `${API_URL}/public/manifest.webmanifest${params.toString() ? `?${params}` : ''}`;

  let link = document.getElementById(MANIFEST_ID);
  if (!link) {
    link = document.createElement('link');
    link.id = MANIFEST_ID;
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  // Re-setting the same href makes some browsers re-fetch for nothing.
  if (link.getAttribute('href') !== href) link.setAttribute('href', href);

  if (themeColor) {
    let meta = document.getElementById(THEME_ID);
    if (!meta) {
      meta = document.createElement('meta');
      meta.id = THEME_ID;
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', themeColor);
  }

  // iOS ignores the manifest's icons and reads this instead.
  const iconHref = `${API_URL}/public/app-icon-192.png${subdomain ? `?tenant=${subdomain}` : ''}`;
  let apple = document.querySelector('link[rel="apple-touch-icon"]');
  if (!apple) {
    apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    document.head.appendChild(apple);
  }
  if (apple.getAttribute('href') !== iconHref) apple.setAttribute('href', iconHref);
}

/* ------------------------------------------------------------------ install prompt */

let deferredPrompt = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => { try { fn(); } catch { /* a bad listener is not our problem */ } });

export function watchInstallability(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function canInstall() {
  return !!deferredPrompt;
}

/** Show the browser's own install dialog. Returns true if they accepted. */
export async function promptInstall() {
  if (!deferredPrompt) return false;
  const prompt = deferredPrompt;
  // It can only be used once, whatever the answer.
  deferredPrompt = null;
  notify();
  try {
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    return outcome === 'accepted';
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ service worker */

let updateReady = null;      // the waiting worker, once one exists
const updateListeners = new Set();

export function watchUpdates(fn) {
  updateListeners.add(fn);
  return () => updateListeners.delete(fn);
}

export function hasUpdate() {
  return !!updateReady;
}

/** Hand over to the new worker and reload — only ever on the person's say-so. */
export function applyUpdate() {
  if (!updateReady) { window.location.reload(); return; }
  updateReady.postMessage({ type: 'SKIP_WAITING' });
  // controllerchange fires once the new worker takes over; reload then, not before.
}

export function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  // CRA's dev server does not serve the worker from the build folder.
  if (process.env.NODE_ENV === 'development' && !window.__FORCE_SW__) return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();           // keep it rather than letting Chrome drop it
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').then((reg) => {
      const watch = (worker) => {
        if (!worker) return;
        worker.addEventListener('statechange', () => {
          // "installed" with a controller already present means this is an UPDATE,
          // not a first install — the first install must not nag anyone to reload.
          if (worker.state === 'installed' && navigator.serviceWorker.controller) {
            updateReady = worker;
            updateListeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
          }
        });
      };
      if (reg.waiting && navigator.serviceWorker.controller) {
        updateReady = reg.waiting;
        updateListeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
      }
      watch(reg.installing);
      reg.addEventListener('updatefound', () => watch(reg.installing));
      // Catch a deploy that lands while the tab is left open.
      setInterval(() => reg.update().catch(() => undefined), 60 * 60 * 1000);
    }).catch(() => undefined);

    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}
